import React, { useState, useEffect } from 'react';
import { AlertTriangle, Phone, Mail, LogOut, Loader2 } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { UserRole } from '@/types/global.types';
import { getPlatformSettingsPublic, type PlatformSettings } from '@/services/platformSettingsService';

/**
 * Page displayed when the institution is suspended (e.g., due to unpaid bills).
 * Shows different messages based on user role:
 * - DG/Financial Manager: See billing-related message
 * - Other users: See generic unavailability message
 */
export const SystemSuspendedPage: React.FC = () => {
  const { user, logout } = useAuthStore();
  const [platformSettings, setPlatformSettings] = useState<PlatformSettings | null>(null);
  const [loadingSettings, setLoadingSettings] = useState(true);

  const isFinancialRole = user?.role === UserRole.DIRECTOR_GENERAL ||
                          user?.role === UserRole.FINANCIAL_MANAGER;

  // Load platform settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await getPlatformSettingsPublic();
        setPlatformSettings(settings);
      } catch (error) {
        console.error('Failed to load platform settings:', error);
        // Use default values if fetch fails
        setPlatformSettings({
          id: '',
          companyName: 'NEXEDU',
          companyLogo: null,
          contactPhone: '+212 5 22 00 00 00',
          contactEmail: 'facturation@nexedu.ma',
          createdAt: '',
          updatedAt: '',
        });
      } finally {
        setLoadingSettings(false);
      }
    };
    loadSettings();
  }, []);

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  // Get contact info with fallback
  const contactPhone = platformSettings?.contactPhone || '+212 5 22 00 00 00';
  const contactEmail = platformSettings?.contactEmail || 'facturation@nexedu.ma';
  const companyName = platformSettings?.companyName || 'NEXEDU';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className={`px-6 py-8 text-center ${
            isFinancialRole
              ? 'bg-gradient-to-r from-amber-500 to-orange-500'
              : 'bg-gradient-to-r from-gray-600 to-gray-700'
          }`}>
            <div className="h-16 w-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={32} className="text-white" />
            </div>
            <h1 className="text-xl font-bold text-white">
              {isFinancialRole
                ? 'Acces Temporairement Suspendu'
                : 'Systeme Temporairement Indisponible'
              }
            </h1>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {isFinancialRole ? (
              <>
                <div className="text-center">
                  <p className="text-gray-600 leading-relaxed">
                    L'acces a votre etablissement <span className="font-semibold">{user?.tenantName}</span> est
                    temporairement suspendu en raison d'un reglement en attente.
                  </p>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h3 className="font-semibold text-amber-800 mb-2">
                    Pour reactiver votre acces:
                  </h3>
                  <ul className="text-sm text-amber-700 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5">1.</span>
                      <span>Regularisez votre situation financiere</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5">2.</span>
                      <span>Contactez notre equipe commerciale pour confirmer le paiement</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5">3.</span>
                      <span>Votre acces sera reactive sous 24h apres validation</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-700 mb-3">Nous contacter</h3>
                  {loadingSettings ? (
                    <div className="flex items-center justify-center py-2">
                      <Loader2 size={20} className="animate-spin text-gray-400" />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <a
                        href={`tel:${contactPhone.replace(/\s/g, '')}`}
                        className="flex items-center gap-3 text-gray-600 hover:text-primary-600 transition-colors"
                      >
                        <Phone size={18} />
                        <span>{contactPhone}</span>
                      </a>
                      <a
                        href={`mailto:${contactEmail}`}
                        className="flex items-center gap-3 text-gray-600 hover:text-primary-600 transition-colors"
                      >
                        <Mail size={18} />
                        <span>{contactEmail}</span>
                      </a>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="text-center">
                  <p className="text-gray-600 leading-relaxed">
                    Le systeme est actuellement indisponible pour votre etablissement.
                  </p>
                  <p className="text-gray-500 mt-2 text-sm">
                    Veuillez contacter votre administration pour plus d'informations.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-500">
                    Si vous pensez qu'il s'agit d'une erreur, veuillez contacter
                    le responsable de votre etablissement.
                  </p>
                </div>
              </>
            )}

            {/* Logout Button */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                <LogOut size={18} />
                Se deconnecter
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          {companyName} - Plateforme de Gestion Educative
        </p>
      </div>
    </div>
  );
};
